'use client';

import React, { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import { useInView } from '@/lib/useInView';

type CarouselItem = {
  src: string;
  width: number;
  alt?: string;
};

type LayoutAboutTitleCarouselMobileProps = {
  carouselData?: CarouselItem[]; // Optional array of carousel items with src and width
};

/**
 * LayoutAboutTitleCarouselMobile - Mobile-only carousel component for the "Join our team" section.
 * 
 * Raw JavaScript implementation for better control over infinite scroll.
 * Note: This carousel is mobile-only (up to 768px). The heading and CTA button are
 * handled by the parent LayoutAboutTitleCarousel component.
 * 
 * @component
 */
export default function LayoutAboutTitleCarouselMobile({ carouselData }: LayoutAboutTitleCarouselMobileProps = {}) {
  const { ref } = useInView<HTMLDivElement>();
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [hasDragged, setHasDragged] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [translateX, setTranslateX] = useState(0);
  const translateXRef = useRef(0);
  const dragStartX = useRef(0);
  const dragStartY = useRef(0);
  const dragStartTranslate = useRef(0);
  const dragDirection = useRef<'horizontal' | 'vertical' | null>(null);
  const animationFrameId = useRef<number | null>(null);
  const setWidthRef = useRef<number | null>(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const checkReducedMotion = () => {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);
    };
    
    checkReducedMotion(); // Initial check
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };
    
    // Listen for changes (some browsers support this)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Extract images from carouselData, or use fallback
  const defaultCarouselData: CarouselItem[] = [
    { src: '/images/about-carousel/about-carousel-desktop-01.jpg', width: 400 },
    { src: '/images/about-carousel/about-carousel-desktop-02.jpg', width: 400 },
    { src: '/images/about-carousel/about-carousel-desktop-03.jpg', width: 400 },
    { src: '/images/about-carousel/about-carousel-desktop-04.jpg', width: 400 },
    { src: '/images/about-carousel/about-carousel-desktop-05.jpg', width: 400 },
    { src: '/images/about-carousel/about-carousel-desktop-06.jpg', width: 400 },
    { src: '/images/about-carousel/about-carousel-desktop-07.jpg', width: 400 },
  ];
  
  const data = carouselData || defaultCarouselData;
  const carouselImages = data.map(item => item.src);
  const carouselAltTexts = data.map((item, index) => item.alt || `Careers image ${index + 1}`);
  
  // Duplicate images for seamless infinite scroll
  const duplicatedImages = [...carouselImages, ...carouselImages];

  // Continuous scroll animation (time-based for smooth performance)
  useEffect(() => {
    // Don't animate if user prefers reduced motion
    if (prefersReducedMotion) {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
      return;
    }
    
    if (!wrapperRef.current || isDragging || hasDragged) {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
      return;
    }

    const scrollSpeed = 60; // pixels per second
    let lastTime: number | null = null;
    let x = translateXRef.current;
    let setWidth: number | null = null;

    const animate = (timestamp: number) => {
      if (!wrapperRef.current || isDragging || hasDragged) {
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
          animationFrameId.current = null;
        }
        return;
      }

      // Calculate set width once on first frame
      if (setWidth === null) {
        const wrapper = wrapperRef.current;
        const firstSlide = wrapper.children[0] as HTMLElement;
        const duplicateStartSlide = wrapper.children[carouselImages.length] as HTMLElement;
        
        if (!firstSlide || !duplicateStartSlide) {
          animationFrameId.current = requestAnimationFrame(animate);
          return;
        }

        const firstSlideRect = firstSlide.getBoundingClientRect();
        const duplicateStartSlideRect = duplicateStartSlide.getBoundingClientRect();
        setWidth = Math.abs(duplicateStartSlideRect.left - firstSlideRect.left);
        setWidthRef.current = setWidth;
      }

      if (lastTime !== null) {
        // Calculate delta time in seconds
        const deltaSeconds = (timestamp - lastTime) / 1000;

        // Move left at `speed` px/sec, independent of FPS
        x -= scrollSpeed * deltaSeconds;

        // If we've scrolled the whole set off-screen, wrap around
        if (setWidth && x <= -setWidth) {
          x += setWidth;
        }

        // Use translate3d for GPU acceleration
        wrapperRef.current.style.transform = `translate3d(${x}px, 0, 0)`;
        translateXRef.current = x;
        // Don't call setTranslateX here - it causes unnecessary re-renders
        // Only update ref and DOM directly for smooth animation
      }

      lastTime = timestamp;
      animationFrameId.current = requestAnimationFrame(animate);
    };

    // Start animation
    animationFrameId.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
    };
  }, [isDragging, hasDragged, carouselImages.length, prefersReducedMotion]);


  // Drag handlers
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    // Don't prevent default yet - wait to determine direction
    // Only prevent for mouse to avoid image dragging
    if (!('touches' in e)) {
      e.preventDefault();
    }
    
    // Stop animation first to prevent race conditions
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
    
    // Read current transform from DOM and IMMEDIATELY reapply it
    // This prevents React re-renders from resetting the position
    if (wrapperRef.current) {
      const style = window.getComputedStyle(wrapperRef.current);
      const transform = style.transform;
      if (transform && transform !== 'none') {
        const matrix = new DOMMatrix(transform);
        const currentTranslate = matrix.m41; // m41 is translateX
        translateXRef.current = currentTranslate;
        // IMMEDIATELY reapply to prevent any shift during state update
        wrapperRef.current.style.transform = `translate3d(${currentTranslate}px, 0, 0)`;
      }
    }
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStartX.current = clientX;
    dragStartY.current = clientY;
    dragStartTranslate.current = translateXRef.current;
    dragDirection.current = null; // Reset direction
    
    // Set state AFTER we've locked in the position
    setHasDragged(true);
    setIsDragging(true);
  };

  const handleDragMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging || !wrapperRef.current) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const deltaX = clientX - dragStartX.current;
    const deltaY = clientY - dragStartY.current;

    // Determine drag direction on first move
    if (dragDirection.current === null) {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      
      // Need at least 5px movement to determine direction
      if (absX > 5 || absY > 5) {
        dragDirection.current = absX > absY ? 'horizontal' : 'vertical';
      } else {
        // Not enough movement yet
        return;
      }
    }

    // If vertical scroll, don't interfere - let the page scroll
    if (dragDirection.current === 'vertical') {
      return;
    }

    // Horizontal drag - prevent default and handle carousel
    e.preventDefault();
    let newTranslate = dragStartTranslate.current + deltaX;

    // Calculate set width if not already calculated
    if (setWidthRef.current === null && wrapperRef.current) {
      const wrapper = wrapperRef.current;
      const firstSlide = wrapper.children[0] as HTMLElement;
      const duplicateStartSlide = wrapper.children[carouselImages.length] as HTMLElement;
      
      if (firstSlide && duplicateStartSlide) {
        // Get wrapper's position to calculate relative positions
        const wrapperRect = wrapper.getBoundingClientRect();
        const firstSlideRect = firstSlide.getBoundingClientRect();
        const duplicateStartSlideRect = duplicateStartSlide.getBoundingClientRect();
        
        // Calculate relative to wrapper to account for current translate
        const firstSlideLeft = firstSlideRect.left - wrapperRect.left;
        const duplicateStartSlideLeft = duplicateStartSlideRect.left - wrapperRect.left;
        setWidthRef.current = Math.abs(duplicateStartSlideLeft - firstSlideLeft);
      }
    }

    // Handle infinite scroll when dragging
    if (setWidthRef.current && setWidthRef.current > 0) {
      // If dragging left past the start of first set, wrap to duplicate set
      if (newTranslate > 0) {
        newTranslate = newTranslate - setWidthRef.current;
        // Update drag start position to maintain smooth dragging
        dragStartTranslate.current = newTranslate;
        dragStartX.current = clientX;
      }
      // If dragging right past the end of duplicate set, wrap to first set
      if (newTranslate < -setWidthRef.current) {
        newTranslate = newTranslate + setWidthRef.current;
        // Update drag start position to maintain smooth dragging
        dragStartTranslate.current = newTranslate;
        dragStartX.current = clientX;
      }
    }

    // Use translate3d for GPU acceleration
    if (wrapperRef.current) {
      wrapperRef.current.style.transform = `translate3d(${newTranslate}px, 0, 0)`;
    }
    translateXRef.current = newTranslate;
    // Don't update state during drag - it causes re-renders and jumps
    // State is only needed for React tracking, but we update DOM directly
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    dragDirection.current = null; // Reset direction
    // No snapping - carousel stays where user releases it
  };

  // Add global drag move and end listeners
  useEffect(() => {
    if (isDragging) {
      const handleMove = (e: MouseEvent | TouchEvent) => handleDragMove(e);
      const handleEnd = () => {
        handleDragEnd();
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      };

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd);

      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging, translateX]);

  return (
    <>
      {/* Mobile-only carousel container (hidden above 768px) */}
      <div className="w-full flex justify-center mb-[2rem] lg:mb-[3.375rem] md:hidden">
        <div className="w-full max-w-[1920px]">
          <div className="w-full">
            <div 
              ref={ref} 
              className="w-full overflow-hidden animate-in-view"
            >
              <div
                ref={containerRef}
                className="w-full overflow-hidden"
                onMouseDown={handleDragStart}
                onTouchStart={handleDragStart}
                style={{ 
                  cursor: isDragging ? 'grabbing' : 'grab',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  touchAction: 'pan-y'
                }}
              >
                <div
                  ref={wrapperRef}
                  className="flex carousel-wrapper"
                  style={{
                    willChange: 'transform',
                  }}
                >
                  {duplicatedImages.map((image, index) => {
                    const originalIndex = index % carouselImages.length;
                    const altText = carouselAltTexts[originalIndex] || `Careers image ${originalIndex + 1}`;
                    
                    return (
                      <div
                        key={index}
                        className="flex-shrink-0 carousel-slide"
                        style={{ 
                          pointerEvents: 'none',
                          userSelect: 'none',
                          WebkitUserSelect: 'none'
                        }}
                      >
                        <div className="relative">
                          <div className="aspect-[5/6] relative overflow-hidden rounded-3xl">
                            <Image
                              src={image}
                              alt={altText}
                              fill
                              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                              className="object-cover rounded-3xl"
                              draggable={false}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .carousel-wrapper {
          will-change: transform;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }

        .carousel-slide {
          /* Mobile: One card per view with next card peeking through on right */
          width: 88%;
          margin-right: 0.75rem;
        }

        /* Small mobile/tablet: 2 cards per view with third peeking */
        @media (min-width: 500px) and (max-width: 767px) {
          .carousel-slide {
            width: calc((100% - 1.25rem) / 2.15);
            margin-right: 0.75rem;
          }
        }

        /* Tablet: Responsive width for 2 slides */
        @media (min-width: 768px) and (max-width: 949px) {
          .carousel-slide {
            width: calc((100% - 1.5625rem) / 2);
            margin-right: 0.9375rem;
          }
        }

        /* Desktop: Responsive width for 3 slides */
        @media (min-width: 950px) and (max-width: 1549px) {
          .carousel-slide {
            width: calc((100% - 5.625rem) / 3);
            margin-right: 1.5625rem;
          }
        }

        /* Large Desktop: Responsive width for 4 slides */
        @media (min-width: 1550px) {
          .carousel-slide {
            width: calc((100% - 8.4375rem) / 4);
            margin-right: 1.5625rem;
          }
        }
      `}</style>
    </>
  );
}
